import React, { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Input } from 'reactstrap'
import { dayDate } from '../../constants/StringsConstant'

import { addWorkDetailsApiFun } from '../../API/Services/authService'
import LoaderWrapper from '../../Components/LoaderWrapper/LoaderWrapper'
import { useAlert } from 'react-alert'
import FieldBadge from '../../Components/Common/badge/FieldBadge'
import { fieldBadgeClassName } from '../../helper/helper'
import settings from '../../config/settings'

/**
 * WorkDetails (Web)
 * Fields: Employee ID, Monthly Salary, Payday (1–31), Work Email, Group
 * - Groups fetched from: GET {settings.api.url}user/all-groups?employerId=<EMPLOYER_ID>
 * - employerId is taken from user prop (user?.employerId)
 * - Sends groupId with the form (FormData)
 */
function WorkDetails({ workDetails, updateData, user }) {
  const alert = useAlert()
  const [loader, setLoader] = useState(false)
  const [groups, setGroups] = useState([])
  const employerId = useMemo(() => user?.employerId || '', [user])

  // Prefer ID from user.group; fallback to workDetails.groupId
  const userGroupId = user?.group?._id ? String(user.group._id) : ''

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    control,
  } = useForm({
    defaultValues: {
      employeeId: workDetails?.employeeId || '',
      salary: workDetails?.salary || '',
      lastDate: workDetails?.salarycreditDay || '',
      email: workDetails?.workEmail || '',
      groupId: userGroupId || (workDetails?.groupId ? String(workDetails.groupId) : ''),
    },
  })

  // ————————— Prefill when props change (prefer user.group)
  useEffect(() => {
    setValue('employeeId', workDetails?.employeeId || '')
    setValue('salary', workDetails?.salary || '')
    setValue('email', workDetails?.workEmail || '')
    setValue('lastDate', workDetails?.salarycreditDay || '')
    setValue('groupId', userGroupId || (workDetails?.groupId ? String(workDetails.groupId) : ''))
  }, [workDetails, userGroupId, setValue])

  // ————————— Fetch Groups using employerId from user prop
  useEffect(() => {
    if (!employerId) return
    let cancelled = false

    async function fetchGroups() {
      try {
        setLoader(true)
        const res = await fetch(
          `${settings.api.url}user/all-groups?employerId=${encodeURIComponent(employerId)}`,
          { headers: { accept: '*/*' } }
        )
        const json = await res.json().catch(() => ({}))

        // Normalise to { id: string, name: string }
        const raw = json?.data?.groups || json?.groups || []
        const normalized = Array.isArray(raw)
          ? raw.map((g, i) => ({
              id: String(g?.id || g?._id || g?.code || i),
              name: g?.name || g?.groupName || g?.label || `Group ${i + 1}`,
            }))
          : []

        if (!cancelled) setGroups(normalized)
      } catch (e) {
        if (!cancelled) alert.error(e?.message || 'Failed to load groups')
      } finally {
        if (!cancelled) setLoader(false)
      }
    }

    fetchGroups()
    return () => { cancelled = true }
  }, [employerId, alert])

  // ————————— Disabled logic (mirrors your fieldStatus usage)
  const hasEmployeeIdVal =
    user?.workDetails?.employeeId && user?.workDetails?.employeeId !== 'undefined'
  const disabledEmployeeId = hasEmployeeIdVal ? user?.fieldStatus?.employeeId == 2 : false
  const disabledSalary = user?.fieldStatus?.salary == 2
  const disabledPayday = user?.fieldStatus?.salarycreditDay == 2
  const hasWorkEmailVal =
    user?.workDetails?.workEmail && user?.workDetails?.workEmail !== 'undefined'
  const disabledEmail = hasWorkEmailVal ? user?.fieldStatus?.workEmail == 2 : false
  // Group stays editable only when not present in user payload
  const disabledGroup = Boolean(user?.group?.name)

  // ————————— Submit
  const shouldSendGroup = (gid) => {
    if (gid === null || gid === undefined) return false
    const s = String(gid).trim().toLowerCase()
    return !(s === '' || s === '0' || s === 'null')
  }

  const onSubmit = (data) => {
    setLoader(true)
    const formData = new FormData()
    formData.append('employeeId', data.employeeId ? data.employeeId : '')
    formData.append('salary', data.salary || '')
    formData.append('salarycreditDay', data.lastDate || '')
    formData.append('workEmail', data.email ? data.email : '')

    if (shouldSendGroup(data.groupId)) {
      formData.append('groupId', String(data.groupId))
    }

    addWorkDetailsApiFun(formData)
      .then((response) => {
        setLoader(false)
        if (response?.data?.status === 'OK' || response?.data?.statusCode === 200) {
          alert.success(response?.data?.message || 'Work details updated')
          updateData && updateData('2')
        } else {
          alert.error(response?.data?.message || 'Update failed')
          // optional: console.log('Error response -->', response)
        }
      })
      .catch((error) => {
        setLoader(false)
        alert.error(error?.message || 'Request failed')
      })
  }

  // Put this helper above your component export
const showSaveBtnWorkDetails = (user) => {
  if (!user?.fieldStatus) return ''; // keep same pattern as your original

  const fs = user.fieldStatus;

  const hasEmployeeIdVal =
    user?.workDetails?.employeeId && user?.workDetails?.employeeId !== 'undefined';
  const lockedEmployeeId = hasEmployeeIdVal ? fs.employeeId == 2 : false;

  const lockedSalary = fs.salary == 2;
  const lockedPayday = fs.salarycreditDay == 2;

  const hasWorkEmailVal =
    user?.workDetails?.workEmail && user?.workDetails?.workEmail !== 'undefined';
  const lockedEmail = hasWorkEmailVal ? fs.workEmail == 2 : false;

  // If API already returns a group (name present), treat group as locked
  const lockedGroup = Boolean(user?.group?.name);

  const allLocked = [lockedEmployeeId, lockedSalary, lockedPayday, lockedEmail, lockedGroup].every(Boolean);

  if (allLocked) return '';

  return (
    <div className="save-btn-wrapper">
      <button type="submit" className="btn primary-btn w-auto px-5">Save</button>
    </div>
  );
};

  return (
    <>
      <div className="fill-details-wrapper">
        <div className="row">
          <div className="col-xl-8 col-lg-10">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="form-wrapper">
                {/* Employee ID */}
                <div className="input-wrapper">
                  <label className="input-label-profile" htmlFor="employee-id">
                    Employer Id: <FieldBadge status={user?.fieldStatus?.employeeId} />
                  </label>
                  <input
                    disabled={!!disabledEmployeeId}
                    type="text"
                    className={`input-box-profile form-control ${
                      errors.employeeId ? 'not-valid' : ''
                    } ${fieldBadgeClassName(user?.fieldStatus?.employeeId)}`}
                    {...register('employeeId')}
                    id="employee-id"
                    placeholder="Employer ID"
                  />
                </div>

                {/* Monthly Salary */}
                <div className="input-wrapper">
                  <label className="input-label-profile" htmlFor="salary">
                    Monthly salary: <FieldBadge status={user?.fieldStatus?.salary} />
                  </label>
                  <input
                    disabled={!!disabledSalary}
                    type="text"
                    inputMode="numeric"
                    className={`input-box-profile form-control ${
                      errors.salary ? 'not-valid' : ''
                    } ${fieldBadgeClassName(user?.fieldStatus?.salary)}`}
                    {...register('salary', {
                      validate: (v) => {
                        if (!v) return 'Monthly salary is required'
                        if (isNaN(Number(v))) return 'Salary can only be in digits'
                        if (Number(v) <= 0) return 'Salary cannot be negative or 0'
                        return true
                      },
                    })}
                    id="salary"
                    placeholder="Monthly salary"
                  />
                  {errors?.salary && (
                    <small className="error-msg">{errors.salary.message}</small>
                  )}
                </div>

                {/* Payday (Month day salary is credited) */}
                <div className="input-wrapper">
                  <label className="input-label-profile" htmlFor="last-date">
                    Month Day on which salary is credited:{' '}
                    <FieldBadge status={user?.fieldStatus?.salarycreditDay} />
                  </label>
                  <Controller
                    control={control}
                    name="lastDate"
                    rules={{ required: 'Please select Payday' }}
                    render={({ field: { onChange, value } }) => (
                      <Input
                        disabled={!!disabledPayday}
                        type="select"
                        className={`form-control input-box-profile ${
                          errors.lastDate ? 'not-valid' : ''
                        } ${fieldBadgeClassName(user?.fieldStatus?.salarycreditDay)}`}
                        value={value || ''}
                        id="last-date"
                        onChange={onChange}
                        style={{ minHeight: 44 }}
                      >
                        <option value="" disabled>
                          Select date
                        </option>
                        {dayDate?.slice(0, 31)?.map((date, index) => (
                          <option value={index + 1} key={index}>
                            {date.day}
                          </option>
                        ))}
                      </Input>
                    )}
                  />
                  {errors.lastDate && (
                    <small className="error-msg">{errors.lastDate.message}</small>
                  )}
                </div>

                {/* Work Email */}
                <div className="input-wrapper">
                  <label className="input-label-profile" htmlFor="email">
                    Work E-mail Address: <FieldBadge status={user?.fieldStatus?.workEmail} />
                  </label>
                  <input
                    disabled={!!disabledEmail}
                    type="email"
                    className={`input-box-profile form-control ${
                      errors.email ? 'not-valid' : ''
                    } ${fieldBadgeClassName(user?.fieldStatus?.workEmail)}`}
                    {...register('email')}
                    id="email"
                    placeholder="Work E-mail Address"
                  />
                </div>

                {/* Group */}
                <div className="input-wrapper">
                  <label className="input-label-profile" htmlFor="groupId">
                    Group
                  </label>

                  {user?.group?.name ? (
                    <>
                      {/* Read-only name from API, still submit the ID */}
                      <input
                        type="text"
                        className="input-box-profile form-control"
                        value={user.group.name}
                        disabled
                        readOnly
                      />
                      <input type="hidden" {...register('groupId')} value={userGroupId} />
                    </>
                  ) : (
                    <Controller
                      control={control}
                      name="groupId"
                      rules={{ required: 'Please select a group' }}
                      render={({ field: { onChange, value } }) => (
                        <Input
                          disabled={!!disabledGroup}
                          type="select"
                          className={`form-control input-box-profile ${
                            errors.groupId ? 'not-valid' : ''
                          }`}
                          id="groupId"
                          value={value || ''}
                          onChange={(e) => onChange(e.target.value)}
                          style={{ minHeight: 44 }}
                        >
                          <option value="" disabled>
                            {groups?.length
                              ? 'Select Group'
                              : employerId
                                ? 'Loading groups...'
                                : 'Employer ID not found'}
                          </option>
                          {groups?.map((g) => (
                            <option value={g.id} key={g.id}>
                              {g.name}
                            </option>
                          ))}
                        </Input>
                      )}
                    />
                  )}

                  {errors.groupId && (
                    <small className="error-msg">{errors.groupId.message}</small>
                  )}
                </div>
              </div>

              {/* Save / Submit */}
              {showSaveBtnWorkDetails(user)}
            </form>
          </div>
        </div>
      </div>

      {loader ? <LoaderWrapper /> : ''}
    </>
  )
}

export default WorkDetails
